import { Skeleton } from "../../components/ui/Skeleton";
import { useWalletBalances } from "../../hooks/useWalletBalances";
import type { WalletBalancesResponse } from "../../lib/api";
import { formatAddress } from "../../lib/format";
import { BalancePill } from "./BalancePill";

/**
 * Sits on top of the Transactions plan in the right column. Shows the
 * MetaWallet address + balances of ETH, USDC, WBTC (and USDe when the
 * chain config exposes it). Read against the configured chain RPC
 * (anvil in dev) — refreshes after a cart Execute lands via the
 * `walletBalances` queryKey.
 */
export function WalletBalanceCard() {
	const { data, error, isLoading } = useWalletBalances();

	return (
		<section className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
			<header className="space-y-0.5">
				<p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
					Wallet
				</p>
				<p
					className="font-mono text-[11px] text-slate-300"
					title={data?.wallet}
				>
					{data ? (
						formatAddress(data.wallet)
					) : (
						<Skeleton className="h-3 w-32" />
					)}
				</p>
			</header>

			{isLoading && <BalancesSkeleton />}
			{error && (
				<p className="rounded border border-rose-900/60 bg-rose-950/40 p-2 text-[11px] text-rose-300">
					{(error as Error).message}
				</p>
			)}
			{data && <BalanceRow data={data} />}
		</section>
	);
}

function BalanceRow({ data }: { data: WalletBalancesResponse }) {
	if (data.balances.length === 0) {
		return (
			<p className="text-[11px] text-slate-500">
				No balances configured for this chain.
			</p>
		);
	}
	return (
		<div className="flex flex-wrap gap-1.5">
			{data.balances.map((b) => (
				<BalancePill key={b.symbol} balance={b} />
			))}
		</div>
	);
}

function BalancesSkeleton() {
	return (
		<div className="flex gap-2">
			<Skeleton className="h-6 w-24 rounded-md" />
			<Skeleton className="h-6 w-24 rounded-md" />
			<Skeleton className="h-6 w-24 rounded-md" />
		</div>
	);
}
